/**
 * @fileoverview Student Detail ("Studio Quiet"). A name-first header band, an
 * explicit Guardian / Student contacts block (who is billed + emailed), a tidied
 * detail rail (stats, next lesson, instruments/address/age/consent), and a gated
 * edit form. Per the Newport Jazz pilot call: the guardian contact and age band
 * are the billing/consent identity, so they are locked by default and unlocking
 * them is a deliberate ("flagged") action — while the student's own email edits
 * inline. Delete, unsaved-changes guard, portal invite, saved repertoires,
 * billing, assignments and consent actions are all preserved.
 */

import { useState, useEffect, useCallback, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { accountFeatureEnabled } from "@/lib/orgFeatures";
import { studentDeleteEnabled, studioLayerEnabled } from "@/lib/featureFlags";
import { Copy, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button as UiButton } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { InstrumentInput } from "@/components/InstrumentInput";
import TagInput from "@/components/TagInput";
import { INTEREST_OPTIONS } from "@/lib/interests";
import { ConsentBadge } from "@/components/ConsentBadge";
import { MarkOfflineConsentDialog } from "@/components/MarkOfflineConsentDialog";
import { WeeklySlotChip } from "@/components/WeeklySlotChip";
import ProgramChip from "@/components/ProgramChip";
import api, { getErrorMessage } from "@/lib/api";
import {
  consentStatusHint, canRequestConsent, canMarkOfflineConsent, canUpdateConsent, ageBandLabel,
  AGE_BAND_OPTIONS, isMinorBand, PARENT_EMAIL_REQUIRED_MESSAGE, consentAudienceHint,
} from "@/lib/consent";
import { formatLessonDuration } from "@/lib/duration";
import { recordedAt, sortChronologically } from "@/lib/lessonHistory";
import { formatPhoneInput } from "@/lib/phone";
import {
  isImportedStub as needsImportedReview, isProgramRosterStudent, realStudentEmail as realEmail, studentContactEmail,
} from "@/lib/importedStudents";
import { toast } from "sonner";
import { ASSIGNMENTS_ENABLED, BILLING_ENABLED, STUDENT_PORTAL_ENABLED, invoiceHistoryEnabled } from "@/lib/featureFlags";
import { StudentBillingCard } from "@/components/StudentBillingCard";
import { StudentAssignmentsCard } from "@/components/StudentAssignmentsCard";
import { StudentPracticeRecordingsCard } from "@/components/StudentPracticeRecordingsCard";
import { practiceRecordingsEnabled } from "@/lib/practiceRecordings";
import { StudentGoalsCard } from "@/components/StudentGoalsCard";
import {
  Card, Avatar, Button, Tag, Icon, SectionHeader, ListRow, StatusPill,
} from "@/components/studio/primitives";

// Edit-form field styling (Studio Quiet tokens; locked rows read dashed + muted).
const fieldCls = "w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] px-3.5 py-2.5 font-sans text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] transition-colors focus:border-[var(--accent-yellow-border-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-yellow-dim)]";
const lockedFieldCls = "cursor-not-allowed !border-dashed !border-[var(--studio-divider)] !bg-[var(--studio-tint)] !text-[var(--text-tertiary)]";

// The edit-form seed for a student record. Used both to fill the form and as
// the pristine snapshot the unsaved-changes guard compares against, so the two
// can never drift (a key present in one but not the other would make every
// edit session look dirty). Suppresses imported placeholder contacts,
// including after booking consent.
const pristineEditData = (s) => ({
  first_name: s.first_name,
  last_name: s.last_name,
  email: studentContactEmail(s),
  age_band: s.age_band || "adult",
  instruments: s.instruments?.join(", ") || "",
  address: s.address || "",
  notes: s.notes || "",
  parent_name: s.parent_name || "",
  parent_email: s.parent_email || "",
  parent_phone: s.parent_phone || "",
  favorite_artist: s.favorite_artist || "",
  interests: s.interests || [],
});

const StudentProfile = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  // Portal INVITES are studio-layer (I7); a portal account already
  // claimed keeps working on every tier — see the badge below.
  const hasStudioLayer = studioLayerEnabled(user);
  // Org turned the repertoire surface off — hide the saved-repertoires section
  // rather than link into a page that 403s (see StudioSidebar nav hiding).
  const repertoireEnabled = accountFeatureEnabled(user, "repertoire");
  // Hans staff switched deletion off platform-wide — hide the button rather
  // than let an instructor confirm a destructive dialog that only 403s.
  const canDeleteStudent = studentDeleteEnabled(user);
  // Rehearsals are reviewed here, not on the lesson (B5); the server's flag
  // decides, and an absent flag reads as off.
  const practiceOn = practiceRecordingsEnabled(user);
  const [student, setStudent] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  // "Flagged" unlock for the guardian + age-band identity fields (call decision).
  const [identityUnlocked, setIdentityUnlocked] = useState(false);
  const [editData, setEditData] = useState({});
  const [inviteUrl, setInviteUrl] = useState(null);
  const [inviteSentTo, setInviteSentTo] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [offlineConsentOpen, setOfflineConsentOpen] = useState(false);
  const [savedRepertoires, setSavedRepertoires] = useState([]);
  const [pendingNav, setPendingNav] = useState(null);

  const isDirty =
    isEditing && student && JSON.stringify(editData) !== JSON.stringify(pristineEditData(student));

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const guardedNavigate = (to) => {
    if (isDirty) setPendingNav(to);
    else navigate(to);
  };

  const fetchStudentData = useCallback(async (signal) => {
    try {
      const [studentRes, lessonsRes, savedRes] = await Promise.all([
        api.get(`/students/${studentId}`, signal ? { signal } : {}),
        api.get(`/students/${studentId}/lessons`, signal ? { signal } : {}),
        repertoireEnabled
          ? api.get(`/repertoire/saved?student_id=${studentId}`, signal ? { signal } : {}).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setStudent(studentRes.data);
      setLessons(sortChronologically(lessonsRes.data, recordedAt));
      setSavedRepertoires(savedRes.data);
      setEditData(pristineEditData(studentRes.data));
    } catch (_error) {
      if (!signal?.aborted) {
        toast.error("Failed to load student");
        navigate("/students");
      }
    } finally {
      setLoading(false);
    }
  }, [studentId, navigate, repertoireEnabled]);

  useEffect(() => {
    const controller = new AbortController();
    fetchStudentData(controller.signal);
    return () => controller.abort();
  }, [studentId, fetchStudentData]);

  const startEdit = () => { setIdentityUnlocked(false); setIsEditing(true); };
  const cancelEdit = () => { setIdentityUnlocked(false); setIsEditing(false); };

  const handleSave = async () => {
    // A program roster's student: the organization keeps their name, contact
    // details and age group, so the form sends only what stays the
    // instructor's (backend services/program_roster_privacy).
    if (isProgramRosterStudent(student)) {
      try {
        await api.put(`/students/${studentId}`, {
          instruments: editData.instruments.split(",").map((i) => i.trim()).filter((i) => i),
          notes: editData.notes || null,
          ...(editData.favorite_artist.trim() ? { favorite_artist: editData.favorite_artist.trim() } : {}),
          interests: editData.interests,
        });
        toast.success("Updated");
        setIsEditing(false);
        fetchStudentData();
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to update"));
      }
      return;
    }
    if (isMinorBand(editData.age_band) && !editData.parent_email?.trim()) {
      toast.error(PARENT_EMAIL_REQUIRED_MESSAGE);
      return;
    }
    try {
      await api.put(`/students/${studentId}`, {
        first_name: editData.first_name,
        last_name: editData.last_name,
        email: editData.email,
        age_band: editData.age_band,
        instruments: editData.instruments.split(",").map((i) => i.trim()).filter((i) => i),
        address: editData.address || null,
        notes: editData.notes || null,
        parent_name: editData.parent_name || null,
        parent_email: editData.parent_email || null,
        // Left out when blank, as iOS does (the server drops nulls anyway).
        ...(editData.parent_phone?.trim() ? { parent_phone: editData.parent_phone.trim() } : {}),
        // Same shapes the iOS app sends (backend `StudentUpdate`): the artist
        // is a plain string and is left out when blank; the interests list
        // always goes, even empty, so removing the last tag clears it.
        ...(editData.favorite_artist.trim() ? { favorite_artist: editData.favorite_artist.trim() } : {}),
        interests: editData.interests,
      });
      toast.success("Updated");
      setIdentityUnlocked(false);
      setIsEditing(false);
      fetchStudentData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update"));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/students/${studentId}`);
      toast.success("Deleted");
      navigate("/students");
    } catch (_error) {
      toast.error("Failed to delete");
    }
  };

  // Archive (I2) is the non-destructive counterpart to Delete: the student and
  // every lesson stay exactly where they are, they just leave the working
  // roster. It needs no confirmation dialog — nothing is lost and Restore sits
  // in the same place — which is precisely the difference worth showing between
  // it and the red Delete button beside it.
  const setArchived = async (archived) => {
    try {
      const res = await api.post(`/students/${studentId}/${archived ? "archive" : "restore"}`);
      setStudent(res.data);
      toast.success(archived ? "Archived" : "Restored to your roster");
    } catch (error) {
      toast.error(getErrorMessage(error, archived ? "Failed to archive" : "Failed to restore"));
    }
  };

  const handleInvite = async () => {
    setInviteLoading(true);
    try {
      const res = await api.post(`/students/${studentId}/invite`);
      setInviteUrl(res.data.invite_url);
      setInviteSentTo(res.data.email_sent ? res.data.sent_to : null);
      if (res.data.email_sent) {
        toast.success(`Invite emailed to ${res.data.sent_to}`);
      } else {
        toast.error("Couldn't send the invite email — share the link below instead.");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to generate invite"));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteUrl);
    setInviteCopied(true);
    toast.success("Invite link copied!");
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const handleResendConsent = async () => {
    setResendLoading(true);
    try {
      const res = await api.post(`/students/${studentId}/consent/resend`);
      toast.success(`The consent email was sent to ${res.data.sent_to}.`);
      fetchStudentData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to send the consent request"));
    } finally {
      setResendLoading(false);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  // `student` can be null with `loading` already false — a fetch aborted by a
  // StrictMode double-mount lands in the finally that flips loading off. Guard
  // the render on both so the identity reads never dereference null.
  if (loading || !student) {
    return (
      <div className="mx-auto max-w-[1000px] px-6 pb-32 pt-8 md:px-12">
        <div className="space-y-4">
          <div className="skeleton h-8 w-48 rounded" />
          <div className="skeleton h-40 rounded-2xl" />
        </div>
      </div>
    );
  }

  const fullName = `${student.first_name || ""} ${student.last_name || ""}`.trim();
  // Unreviewed scheduling import: minted from an unmatched appointment, awaiting
  // the instructor to set the real email + age band before consent/recording.
  const isImportedStub = needsImportedReview(student);
  // On an organization's program roster: shown as "Maya R." (the server
  // shortens it), with no contact details, no consent request and no invite.
  const programRoster = isProgramRosterStudent(student);
  const lastLessonDate = lessons.length
    ? formatDate(lessons.reduce((a, b) => (new Date(a.created_at) > new Date(b.created_at) ? a : b)).created_at)
    : "—";

  // The guardian/age identity fields are editable when the instructor has unlocked
  // them, or when promoting a stub (which has no identity set yet).
  const identityEditable = identityUnlocked || isImportedStub;
  const minor = isMinorBand(student.age_band);
  const guardianOnFile = student.parent_name || realEmail(student.parent_email) || student.parent_phone;

  return (
    <div className="mx-auto max-w-[1080px] px-6 pb-32 pt-8 md:px-12" data-testid="student-profile">
      {/* Top bar */}
      <div className="mb-7 flex items-center justify-between">
        <Button variant="bare" icon="chevronLeft" onPress={() => guardedNavigate("/students")} data-testid="back-to-students">
          Back
        </Button>
        <div className="flex gap-2.5">
          {isEditing ? (
            <>
              <Button variant="bare" icon="x" onPress={cancelEdit}>Cancel</Button>
              <Button icon="check" onPress={handleSave} data-testid="save-changes-button">Save</Button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={startEdit}
                data-testid="edit-student-button"
                aria-label="Edit student"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)]"
              >
                <Icon name="edit" size={18} />
              </button>
              <button
                type="button"
                onClick={() => setArchived(!student.archived)}
                data-testid={student.archived ? "restore-student-button" : "archive-student-button"}
                aria-label={student.archived ? "Restore student" : "Archive student"}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)]"
              >
                <Icon name={student.archived ? "archiveRestore" : "archive"} size={18} />
              </button>
              {/* Indentation of the dialog below is deliberately left as-is:
                  re-indenting these legacy lines would re-present their raw
                  Tailwind utilities to brand-lint as new code. */}
              {canDeleteStudent && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    data-testid="delete-student-button"
                    aria-label="Delete student"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--state-danger-border)] bg-transparent text-[var(--state-danger)] transition-colors hover:bg-[var(--state-danger-dim)]"
                  >
                    <Icon name="trash" size={18} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="studio bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] mx-4 max-w-[calc(100%-2rem)]">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Student?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[var(--text-secondary)]">This will permanently delete {student.first_name} {student.last_name} and all lessons.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-[var(--studio-interactive)] border-[var(--studio-divider-strong)] text-[var(--text-primary)]">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-[var(--state-danger)] text-[var(--text-on-state)]">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )}
            </>
          )}
        </div>
      </div>

      {/* Discard-unsaved-changes guard */}
      <AlertDialog open={!!pendingNav} onOpenChange={(open) => { if (!open) setPendingNav(null); }}>
        <AlertDialogContent className="studio bg-[var(--studio-overlay)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] mx-4 max-w-[calc(100%-2rem)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">You have unsaved edits to this student. Leaving now will discard them.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[var(--studio-interactive)] border-[var(--studio-divider-strong)] text-[var(--text-primary)]">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const to = pendingNav; setPendingNav(null); setIsEditing(false); navigate(to); }} className="bg-[var(--state-danger)] text-[var(--text-on-state)]" data-testid="discard-changes-confirm">Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isImportedStub && !isEditing && (
        <div
          data-testid="acuity-import-banner"
          className="mb-6 rounded-2xl border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-4"
        >
          <div className="font-sans text-sm font-semibold text-[var(--accent-yellow-text)]">Imported from scheduling — needs review</div>
          <p className="mt-1 font-sans text-[13px] text-[var(--text-secondary)]">
            Hans created this student from a booking
            {student.source === "acuity_import" && student.acuity_booker_email ? <> booked by <span className="font-mono">{student.acuity_booker_email}</span></> : null}.
            Review their contact details and age group before recording or requesting consent.
          </p>
          <button
            type="button"
            onClick={startEdit}
            className="mt-3 font-sans text-sm font-medium text-[var(--accent-yellow-text)] hover:underline"
            data-testid="promote-stub-button"
          >
            Review &amp; complete
          </button>
        </div>
      )}

      {isEditing && programRoster ? (
        /* ============ EDIT: a program roster's student ============ */
        <div className="mx-auto max-w-[680px]" data-testid="program-roster-edit">
          <Card className="flex flex-col gap-0">
            <p className="pb-4 font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]" data-testid="program-roster-edit-note">
              {student.first_name} is on a program roster, so the organization keeps their name, contact details and
              age group. Their instruments and your notes are yours to keep here.
            </p>
            <FormGroup label="Lesson details">
              <Field label="Instruments">
                <InstrumentInput value={editData.instruments} onChange={(e) => setEditData({ ...editData, instruments: e.target.value })} className={fieldCls} data-testid="edit-instruments-input" />
              </Field>
              <Field label="Notes">
                <Textarea value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} rows={3} placeholder="Private notes about this student" className={`${fieldCls} min-h-[80px]`} data-testid="edit-notes-input" />
              </Field>
            </FormGroup>
            <FormGroup label="Musical profile">
              <Field label="Favorite artist">
                <Input value={editData.favorite_artist} onChange={(e) => setEditData({ ...editData, favorite_artist: e.target.value })} placeholder="e.g. Chopin, Miles Davis, Taylor Swift…" className={fieldCls} data-testid="edit-favorite-artist-input" />
              </Field>
              <Field label="Musical interests">
                <div data-testid="edit-interests-input">
                  <TagInput value={editData.interests} onChange={(v) => setEditData({ ...editData, interests: v })} suggestions={INTEREST_OPTIONS} placeholder="e.g. Jazz, Songwriting…" />
                </div>
              </Field>
            </FormGroup>
          </Card>
        </div>
      ) : isEditing ? (
        /* ================= EDIT ================= */
        <div className="mx-auto max-w-[680px]">
          <Card className="flex flex-col gap-0">
            {/* Student */}
            <FormGroup label="Student">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name">
                  <Input value={editData.first_name} onChange={(e) => setEditData({ ...editData, first_name: e.target.value })} placeholder="First name" className={fieldCls} data-testid="edit-first-name-input" />
                </Field>
                <Field label="Last name">
                  <Input value={editData.last_name} onChange={(e) => setEditData({ ...editData, last_name: e.target.value })} placeholder="Last name" className={fieldCls} data-testid="edit-last-name-input" />
                </Field>
              </div>
              <Field label="Student email">
                <Input value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} placeholder="student@email.com" className={fieldCls} data-testid="edit-email-input" />
              </Field>
            </FormGroup>

            {/* Guardian (minors) — the billing/consent contact, locked by default.
                For adults there is no guardian, so it's an optional extra contact. */}
            {isMinorBand(editData.age_band) ? (
              <FormGroup
                label="Guardian"
                locked={!identityEditable}
                onUnlock={() => setIdentityUnlocked(true)}
              >
                <Field label="Guardian name">
                  <Input value={editData.parent_name} onChange={(e) => setEditData({ ...editData, parent_name: e.target.value })} placeholder="Parent / guardian name" disabled={!identityEditable} className={`${fieldCls} ${identityEditable ? "" : lockedFieldCls}`} data-testid="edit-parent-name-input" />
                </Field>
                <Field label="Guardian email">
                  <Input value={editData.parent_email} onChange={(e) => setEditData({ ...editData, parent_email: e.target.value })} placeholder="Parent / guardian email" required disabled={!identityEditable} className={`${fieldCls} ${identityEditable ? "" : lockedFieldCls}`} data-testid="edit-parent-email-input" />
                </Field>
                {/* The consent-SMS target, so it locks with the rest of the guardian identity. */}
                <Field label="Guardian phone">
                  <Input type="tel" inputMode="tel" autoComplete="tel" value={editData.parent_phone} onChange={(e) => setEditData({ ...editData, parent_phone: formatPhoneInput(e.target.value) })} placeholder="(555) 555-5555" disabled={!identityEditable} className={`${fieldCls} ${identityEditable ? "" : lockedFieldCls}`} data-testid="edit-parent-phone-input" />
                </Field>
                <p className={helpCls}>
                  The billing &amp; consent contact — they receive every summary email.
                  {identityEditable ? "" : " Changing it is a flagged action, not an inline edit."}
                </p>
              </FormGroup>
            ) : (
              <FormGroup label="Additional contact (optional)">
                <Field label="Name">
                  <Input value={editData.parent_name} onChange={(e) => setEditData({ ...editData, parent_name: e.target.value })} placeholder="Name (optional)" className={fieldCls} data-testid="edit-parent-name-input" />
                </Field>
                <Field label="Additional email">
                  <Input value={editData.parent_email} onChange={(e) => setEditData({ ...editData, parent_email: e.target.value })} placeholder="Additional email (optional)" className={fieldCls} data-testid="edit-parent-email-input" />
                </Field>
                <Field label="Phone">
                  <Input type="tel" inputMode="tel" autoComplete="tel" value={editData.parent_phone} onChange={(e) => setEditData({ ...editData, parent_phone: formatPhoneInput(e.target.value) })} placeholder="(555) 555-5555" className={fieldCls} data-testid="edit-parent-phone-input" />
                </Field>
              </FormGroup>
            )}

            {/* Lesson details */}
            <FormGroup label="Lesson details">
              <Field label="Instruments">
                <InstrumentInput value={editData.instruments} onChange={(e) => setEditData({ ...editData, instruments: e.target.value })} className={fieldCls} data-testid="edit-instruments-input" />
              </Field>
              <Field label="Address">
                <Input value={editData.address} onChange={(e) => setEditData({ ...editData, address: e.target.value })} placeholder="Address" className={fieldCls} data-testid="edit-address-input" />
              </Field>
              <Field label="Notes">
                <Textarea value={editData.notes} onChange={(e) => setEditData({ ...editData, notes: e.target.value })} rows={3} placeholder="Private notes about this student" className={`${fieldCls} min-h-[80px]`} data-testid="edit-notes-input" />
              </Field>
            </FormGroup>

            {/* Musical profile — what the student likes, shown read-only in the
                detail rail. A portal student sets these on claim; the teacher
                can fill them in (or fix them) here, as on iOS. */}
            <FormGroup label="Musical profile">
              <Field label="Favorite artist">
                <Input value={editData.favorite_artist} onChange={(e) => setEditData({ ...editData, favorite_artist: e.target.value })} placeholder="e.g. Chopin, Miles Davis, Taylor Swift…" className={fieldCls} data-testid="edit-favorite-artist-input" />
              </Field>
              <Field label="Musical interests">
                <div data-testid="edit-interests-input">
                  <TagInput value={editData.interests} onChange={(v) => setEditData({ ...editData, interests: v })} suggestions={INTEREST_OPTIONS} placeholder="e.g. Jazz, Songwriting…" />
                </div>
                <p className={helpCls}>Pick from the suggestions or type your own and press Enter.</p>
              </Field>
            </FormGroup>

            {/* Age group — fixed at signup, locked by default. */}
            <FormGroup
              label="Age group"
              locked={!identityEditable}
              onUnlock={() => setIdentityUnlocked(true)}
            >
              <select
                value={editData.age_band}
                onChange={(e) => setEditData({ ...editData, age_band: e.target.value })}
                disabled={!identityEditable}
                className={`${fieldCls} appearance-none ${identityEditable ? "" : lockedFieldCls}`}
                data-testid="edit-age-band-select"
              >
                {AGE_BAND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className={helpCls}>
                {identityEditable ? consentAudienceHint(editData.age_band) : "Age band drives the COPPA consent path, so it's fixed after signup."}
              </p>
            </FormGroup>
          </Card>
        </div>
      ) : (
        /* ================= VIEW ================= */
        <>
          {/* Header band — name-first identity + primary actions */}
          <Card className="flex flex-col gap-5 md:flex-row md:items-center md:gap-6">
            <div className="flex min-w-0 items-center gap-4 md:gap-5">
              <Avatar name={fullName} size={72} accent />
              <div className="min-w-0">
                <h1 className="font-serif text-[26px] font-semibold leading-tight tracking-[-0.02em] text-[var(--text-primary)] md:text-[30px]">{fullName}</h1>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {student.archived && (
                    <Tag className="border-[var(--studio-divider-strong)] text-[var(--text-tertiary)]" data-testid="archived-tag">
                      <Icon name="archive" size={12} /> Archived
                    </Tag>
                  )}
                  <ConsentBadge status={student.consent_status} programRoster={programRoster} />
                  {student.age_band && <Tag>{ageBandLabel(student.age_band)}</Tag>}
                  {student.instruments?.slice(0, 2).map((i) => <Tag key={i}>{i}</Tag>)}
                </div>
              </div>
            </div>
            {/* An archived student's lesson history stays fully readable below —
                only the two "start a new lesson" actions are swapped for the way
                back, since the server refuses both (student_archived). Offering
                Record here would be a button that can only produce a toast. */}
            {student.archived ? (
              <div className="flex flex-col gap-2 md:ml-auto md:w-[240px] md:shrink-0">
                <Button icon="archiveRestore" fullWidth onPress={() => setArchived(false)} data-testid="restore-student-cta">
                  Restore to roster
                </Button>
                <p className="text-center font-sans text-[13px] leading-snug text-[var(--text-tertiary)]">
                  Every lesson is kept. Restore to record again.
                </p>
              </div>
            ) : (
            <div className="flex flex-col gap-2.5 md:ml-auto md:w-[212px] md:shrink-0">
              <div className="flex gap-2.5">
                <Button icon="mic" fullWidth onPress={() => navigate(`/hans/${studentId}`)} className="flex-1" data-testid="record-lesson-button">Record</Button>
                {STUDENT_PORTAL_ENABLED && student.student_account_id ? (
                  // A claimed portal account keeps working on every tier (I7):
                  // it belongs to the family, not to the teacher's plan, so
                  // this badge appears for a free instructor too.
                  <span className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[var(--state-success-border)] bg-[var(--state-success-dim)] px-3 font-sans text-[13px] text-[var(--state-success)]">
                    <Icon name="graduation" size={14} /> Portal
                  </span>
                ) : hasStudioLayer && !programRoster ? (
                  <Button variant="ghost" icon="link" onPress={handleInvite} disabled={inviteLoading} className="flex-1">
                    {inviteLoading ? "…" : "Invite"}
                  </Button>
                ) : null}
              </div>
              {/* Spec 10: type up a lesson without recording (works at any consent state) */}
              <Button variant="ghost" icon="edit" fullWidth onPress={() => navigate(`/lesson/new?student=${studentId}`)} data-testid="add-lesson-notes-button">
                Add lesson notes
              </Button>
            </div>
            )}
          </Card>

          {/* Content — primary column (contacts, history) + detail rail */}
          <div className="mt-6 grid grid-cols-1 items-start gap-6 lg:grid-cols-[1.6fr_1fr]">
            {/* Primary column */}
            <div className="flex min-w-0 flex-col gap-6">
              {/* Contacts — who is billed and emailed */}
              <Card>
                <CardHead>Contacts</CardHead>
                {programRoster ? (
                  <p className="font-sans text-sm text-[var(--text-tertiary)]" data-testid="program-roster-contacts">
                    On a program roster. The organization keeps this student&rsquo;s contact details.
                  </p>
                ) : minor && guardianOnFile ? (
                  <>
                    <Contact role="Guardian" badge="Billed · emailed" locked name={student.parent_name || "—"} email={realEmail(student.parent_email)} phone={student.parent_phone} emptyEmail="No guardian email on file" />
                    <Contact role="Student" badge="Optional" badgeAccent={false} name={fullName} email={realEmail(student.email)} emptyEmail="No email on file" className="mt-4 border-t border-[var(--studio-divider)] pt-4" />
                  </>
                ) : (
                  <>
                    <Contact role="Student" badge="Emailed" name={fullName} email={realEmail(student.email)} emptyEmail="No email on file" />
                    {guardianOnFile && (
                      <Contact role="Additional contact" badgeAccent={false} name={student.parent_name || "—"} email={realEmail(student.parent_email)} phone={student.parent_phone} emptyEmail="—" className="mt-4 border-t border-[var(--studio-divider)] pt-4" />
                    )}
                  </>
                )}
              </Card>

              {/* Goals (Spec 07) — above the assignments, because a goal is
                  what this week's assignments are for. */}
              <StudentGoalsCard studentId={studentId} />

              {/* Practice assignments (Spec 01). "Assign practice" offers items
                  from the lessons the page already holds (B5). */}
              {ASSIGNMENTS_ENABLED && (
                <StudentAssignmentsCard studentId={studentId} lessons={lessons} rehearsalsOn={practiceOn} />
              )}

              {/* The student's rehearsals, for feedback (B5) */}
              <StudentPracticeRecordingsCard studentId={studentId} enabled={practiceOn} />

              {/* Lesson history */}
              <div>
                <SectionHeader>Lesson history</SectionHeader>
                {lessons.length === 0 ? (
                  <Card className="text-center" style={{ padding: "40px 20px" }}>
                    <Icon name="mic" size={30} className="mx-auto mb-3 text-[var(--text-quaternary)]" />
                    <p className="font-sans text-sm text-[var(--text-tertiary)]">No lessons recorded</p>
                  </Card>
                ) : (
                  <Card padding={6}>
                    {lessons.map((lesson, i) => (
                      <ListRow
                        key={lesson.lesson_id}
                        data-testid={`lesson-${lesson.lesson_id}`}
                        leading={<div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--studio-interactive)]"><Icon name="music" size={16} className="text-[var(--text-secondary)]" /></div>}
                        title={
                          <span className="inline-flex items-center gap-2">
                            {lesson.title}
                            {lesson.kind === "class" && <span className="shrink-0 rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-1.5 py-0.5 text-[10px] text-[var(--accent-yellow-text)]">Class</span>}
                          </span>
                        }
                        subtitle={<>{formatDate(lesson.created_at)} <ProgramChip lesson={lesson} programTitle={lesson.program_title} orgName={lesson.org_name} accent={lesson.org_accent} /></>}
                        meta={formatLessonDuration(lesson.duration_seconds)}
                        trailing={lesson.email_sent ? <StatusPill kind="Sent" /> : null}
                        borderBottom={i < lessons.length - 1}
                        onPress={() => navigate(lesson.kind === "class" ? `/lesson/${lesson.lesson_id}?kind=class&readOnly=1` : `/lesson/${lesson.lesson_id}`)}
                      />
                    ))}
                  </Card>
                )}
              </div>
            </div>

            {/* Detail rail */}
            <div className="flex flex-col gap-6">
              {/* At-a-glance stats */}
              <Card padding={0}>
                <div className="flex">
                  {[{ label: "LESSONS", value: lessons.length }, { label: "LAST", value: lastLessonDate }].map((st, i) => (
                    <div key={st.label} className={`flex-1 px-2 py-[18px] text-center ${i === 1 ? "border-l border-[var(--studio-divider)]" : ""}`}>
                      <div className="font-mono text-[22px] font-light text-[var(--text-primary)]">{st.value}</div>
                      <div className="mt-1 font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">{st.label}</div>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Next lesson (Spec 04) — management lives on /schedule */}
              <WeeklySlotChip studentId={studentId} />

              {/* Details */}
              <Card className="flex flex-col gap-5">
                <Detail label="Instruments">
                  {student.instruments?.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">{student.instruments.map((i) => <Tag key={i}>{i}</Tag>)}</div>
                  ) : <Muted>None listed</Muted>}
                </Detail>

                {!programRoster && <Detail label="Address">
                  {student.address ? (
                    <p className="mt-2 flex items-center gap-2 font-sans text-sm text-[var(--text-secondary)]"><Icon name="mapPin" size={14} className="text-[var(--text-tertiary)]" /> {student.address}</p>
                  ) : <Muted>None listed</Muted>}
                </Detail>}

                <Detail label="Notes">
                  {student.notes ? (
                    <p className="mt-2 font-sans text-sm text-[var(--text-secondary)]">{student.notes}</p>
                  ) : <Muted>No notes</Muted>}
                </Detail>

                <div data-testid="age-band-section">
                  <DetailLabel>Age Group</DetailLabel>
                  <p className="mt-2 font-sans text-sm text-[var(--text-secondary)]">{ageBandLabel(student.age_band) || <Muted>Not set</Muted>}</p>
                </div>

                <div data-testid="consent-section">
                  <DetailLabel>Recording Consent</DetailLabel>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {/* Status pill lives in the identity card above (single source). */}
                    {canRequestConsent(student) && (
                      <UiButton variant="outline" size="sm" onClick={handleResendConsent} disabled={resendLoading} className="rounded-full border-[var(--studio-divider-strong)] text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)]" data-testid="resend-consent-button">
                        {resendLoading ? "Sending..." : student.consent_status === "requested" ? "Resend" : "Request consent"}
                      </UiButton>
                    )}
                    {canMarkOfflineConsent(student) && (
                      <UiButton variant="outline" size="sm" onClick={() => setOfflineConsentOpen(true)} className="rounded-full border-[var(--studio-divider-strong)] text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)]" data-testid="mark-offline-consent-button">
                        Mark as consented (offline)
                      </UiButton>
                    )}
                    {canUpdateConsent(student) && (
                      <UiButton variant="outline" size="sm" onClick={() => setOfflineConsentOpen(true)} className="rounded-full border-[var(--studio-divider-strong)] text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)]" data-testid="update-consent-button">
                        Update to current terms
                      </UiButton>
                    )}
                  </div>
                  {canUpdateConsent(student) && (
                    <p className="mt-2 font-sans text-xs text-[var(--text-tertiary)]" data-testid="consent-update-hint">
                      Consented under earlier recording terms. Their consent stands — updating only
                      records that you have confirmed what has changed since.
                    </p>
                  )}
                  {consentStatusHint(student) && (
                    <p className="mt-2 font-sans text-xs text-[var(--text-tertiary)]" data-testid={programRoster ? "program-roster-consent-hint" : undefined}>
                      {consentStatusHint(student)}
                    </p>
                  )}
                  <MarkOfflineConsentDialog student={student} open={offlineConsentOpen} onOpenChange={setOfflineConsentOpen} onConfirmed={fetchStudentData} />
                </div>

                {(student.favorite_artist || student.interests?.length > 0) && (
                  <div className="flex flex-col gap-4 border-t border-[var(--studio-divider)] pt-4">
                    {student.favorite_artist && (
                      <Detail label="Favorite Artist"><p className="mt-2 font-sans text-sm text-[var(--text-secondary)]">{student.favorite_artist}</p></Detail>
                    )}
                    {student.interests?.length > 0 && (
                      <Detail label="Musical Interests">
                        <div className="mt-2 flex flex-wrap gap-1.5">{student.interests.map((it) => <Tag key={it}>{it}</Tag>)}</div>
                      </Detail>
                    )}
                  </div>
                )}
              </Card>

              {BILLING_ENABLED && invoiceHistoryEnabled(user) && <StudentBillingCard studentId={studentId} />}

              {/* Invite link panel */}
              {STUDENT_PORTAL_ENABLED && inviteUrl && (
                <Card padding={16}>
                  <div className="mb-2 flex items-center gap-2">
                    <Icon name="link" size={16} className="text-[var(--accent-yellow-text)]" />
                    <h3 className="font-sans text-sm font-medium text-[var(--text-primary)]">Student Invite Link</h3>
                  </div>
                  <p className="mb-3 font-sans text-xs text-[var(--text-tertiary)]">
                    {inviteSentTo
                      ? `Emailed to ${inviteSentTo}. Expires in 7 days — copy the link if it doesn't arrive.`
                      : `Share with ${fullName}. Expires in 7 days.`}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 truncate rounded-lg bg-[var(--studio-interactive)] px-3 py-2 font-mono text-xs text-[var(--text-tertiary)]">{inviteUrl}</div>
                    <UiButton onClick={handleCopyInvite} size="sm" className="rounded-lg bg-[var(--studio-interactive)] px-3 py-2 text-[var(--text-primary)] hover:bg-[var(--studio-overlay)]" aria-label="Copy invite link">
                      {inviteCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </UiButton>
                  </div>
                </Card>
              )}

              {/* Saved repertoires — hidden when an org disabled the feature, since
                  every link here leads to a page that 403s. */}
              {repertoireEnabled && (
                <div>
                  <SectionHeader>Saved repertoires</SectionHeader>
                  {savedRepertoires.length === 0 ? (
                    <Card className="text-center" style={{ padding: "40px 20px" }}>
                      <Icon name="book" size={30} className="mx-auto mb-3 text-[var(--text-quaternary)]" />
                      <p className="font-sans text-sm text-[var(--text-tertiary)]">No saved repertoires yet</p>
                      <button onClick={() => navigate("/repertoire")} className="mt-3 font-sans text-sm text-[var(--accent-yellow-text)] hover:underline">Create one in Repertoires</button>
                    </Card>
                  ) : (
                    <Card padding={6}>
                      {savedRepertoires.map((item, i) => (
                        <ListRow
                          key={item.repertoire_id}
                          leading={<div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--studio-interactive)]"><Icon name="book" size={16} className="text-[var(--accent-yellow-text)]" /></div>}
                          title={item.title}
                          subtitle={[item.type, new Date(item.created_at).toLocaleDateString(), item.student_id === studentId ? "Tailored" : null].filter(Boolean).join(" · ")}
                          trailing={<Icon name="chevronRight" size={16} className="text-[var(--text-tertiary)]" />}
                          borderBottom={i < savedRepertoires.length - 1}
                          onPress={() => navigate("/repertoire", { state: { viewRepertoire: item } })}
                        />
                      ))}
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

    </div>
  );
};

const helpCls = "mt-1 font-sans text-[11.5px] leading-relaxed text-[var(--text-quaternary)]";

const DetailLabel = ({ children }) => (
  <Label className="font-mono text-[10px] font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">{children}</Label>
);
const Detail = ({ label, children }) => (
  <div><DetailLabel>{label}</DetailLabel>{children}</div>
);
const Muted = ({ children }) => <p className="mt-2 font-sans text-sm text-[var(--text-quaternary)]">{children}</p>;

// Card eyebrow label (mono, uppercase).
const CardHead = ({ children }) => (
  <div className="mb-4 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-[var(--text-tertiary)]">{children}</div>
);

// A labeled contact line (Guardian / Student / Additional) with an optional badge
// and lock hint. `emptyEmail` renders when no real email is on file. `phone`
// (guardian only) renders as a tel: link; the href carries just the digits so
// the dialer never sees the display punctuation.
const Contact = ({ role, badge, badgeAccent = true, locked = false, name, email, phone, emptyEmail, className }) => (
  <div className={className}>
    <div className="mb-1.5 flex items-center gap-2">
      <span className="font-sans text-[13px] font-semibold text-[var(--text-primary)]">{role}</span>
      {badge && (
        <span className={`rounded-[5px] px-[7px] py-[3px] font-mono text-[9.5px] uppercase tracking-[0.06em] ${badgeAccent ? "bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]" : "bg-[var(--studio-interactive)] text-[var(--text-secondary)]"}`}>{badge}</span>
      )}
      {locked && <Icon name="lock" size={12} className="text-[var(--text-tertiary)]" />}
    </div>
    {name && <p className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{name}</p>}
    {email ? (
      <p className="mt-0.5 break-all font-mono text-[12.5px] text-[var(--text-secondary)]">{email}</p>
    ) : (
      <p className="mt-0.5 font-sans text-[13px] italic text-[var(--text-tertiary)]">{emptyEmail}</p>
    )}
    {phone && (
      <a
        href={`tel:${String(phone).replace(/[^\d+]/g, "")}`}
        className="mt-0.5 block font-mono text-[12.5px] text-[var(--text-secondary)] hover:text-[var(--accent-yellow-text)] hover:underline"
        data-testid="contact-phone-link"
      >
        {phone}
      </a>
    )}
  </div>
);

// Edit-form section. When `locked`, the header shows a lock + a "Change" button
// that flips the fields editable (the "flagged" identity edit from the call).
const FormGroup = ({ label, locked, onUnlock, children }) => (
  <div className="border-t border-[var(--studio-divider)] pt-5 first:border-t-0 first:pt-0">
    <div className="mb-3 flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 font-mono text-[10.5px] font-medium uppercase tracking-[0.11em] text-[var(--text-tertiary)]">
        {locked && <Icon name="lock" size={12} className="text-[var(--text-quaternary)]" />}
        {label}
      </span>
      {locked && onUnlock && (
        <button type="button" onClick={onUnlock} className="font-sans text-[11px] font-medium text-[var(--accent-yellow-text)] hover:underline" data-testid="unlock-identity-button">
          Change
        </button>
      )}
    </div>
    <div className="flex flex-col gap-3">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    {label && <span className="mb-1.5 block font-sans text-[12px] font-medium text-[var(--text-secondary)]">{label}</span>}
    {children}
  </div>
);

export default StudentProfile;
